import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { Radio, RadioGroup, Form } from 'informed';
import Card from '../../../components/Card';
import { FieldSet, InputTypeArea, KeyValue, Spinner } from '../../../elements';
import Axios, { AxiosError } from 'axios';
import { match } from 'react-router-dom';
import { History } from 'history';
import { API_URL } from '../../../../lib/const';
import { IApplication, ICoordinationDocumentGroup } from '../types';
import formater from '../../common/formater';
import DocumentsView from '../../../enrollment/components/DocumentsView';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TransferApplicationGradeInput from '../components/TransferApplicationGradeInput';
import { toast } from 'react-toastify';
import { IFormApi } from '../../types';
import { Documents } from '../../../components';
import { handleDocumentUpload } from '../../../services/upload_service';
import { get } from 'lodash';
import { PermissionContext } from '../../../../hoc/withPermission';
import consts from '../consts';
import { issueLabel } from '../components/issueLabel';
import CoordinationDocumentCard from '../components/CoordinationDocumentCard';
import { fetchCoordinationDocuments } from '../services/transferApplicationService';

const TransferApplicationAcademicReviewPage =({ history, match }: { history: History, match: match<{ token: string }> }) => {
  let previousErrors: any = {};

  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState<IApplication>();
  const [submiting, setSubmitting] = useState(false);
  const [curriculumAdaptationDocument, setCurriculumAdaptationDocument] = useState<{ content: any, error: boolean }>({ content: undefined, error: false })
  const [coordinationDocuments, setCoordinationDocuments] = useState<ICoordinationDocumentGroup[]>([]);
  const missingCoordinationDocuments = useMemo(
    () => coordinationDocuments.filter(group => !group.current_document).map(group => group.title),
    [coordinationDocuments],
  );
  const token = match.params.token;

  const fetchApplication = () => {
    setLoading(true);

    Axios.get(`${API_URL}/transfers/applications/${token}`)
      .then(response => setApplication(response.data))
      .catch((error: AxiosError) => {
        if (error.response && error.response.status === 404) {
          history.push('/transferencias/aplicacoes');
        }
      })
      .finally(() => setLoading(false))
  }

  const fetchCoordinationDocumentHistory = useCallback(async () => {
    if (!token) return;

    try {
      const groups = await fetchCoordinationDocuments(token);
      setCoordinationDocuments(groups || []);
    } catch (error) {
      toast.error('Não foi possível carregar os documentos da Coordenação.');
    }
  }, [token]);

  const handleUploadCurriculumAdaptationDocument = useCallback(async (event: InputEvent) => {
    if (!application) return

    const file = get(event, "target.files.0");

    try {
      const response = await handleDocumentUpload(file, application.curriculum_adaptation_document_type.id);
      const { key } = response;

      setCurriculumAdaptationDocument({
        content: {
          key,
          name: file.name,
          url: `${API_URL}/uploads/${key}`,
          document_type: application.curriculum_adaptation_document_type.id,
          approved: null,
        },
        error: false,
      })

    } catch (error) {
      toast.error("Ocorreu um erro ao enviar arquivo. Tente novamente.");
    }
  }, [application]);

  const [sendingForReview, setSendingForReview] = useState(false);

  const handleSendCoordinationDocumentsForReview = useCallback(() => {
    if (!application) return;

    setSendingForReview(true);
    Axios.post(`${API_URL}/transfers/applications/${application.token}/send_coordination_documents_for_review`)
      .then(() => {
        toast.success('Documentos enviados para a análise final da Secretaria.');
        fetchApplication();
      })
      .catch(error => {
        const response = (error as AxiosError).response;
        const message = get(response, 'data.non_field_errors.0');
        toast.error(message || 'Não foi possível enviar os documentos para análise.');
      })
      .finally(() => setSendingForReview(false));
  }, [application]);

  const handleDeferApplication = useCallback((values: any, formApi: IFormApi) => {
    if (!application) return

    Axios.post(`${API_URL}/transfers/applications/${application.token}/defer`,
      {
        ...values,
        curriculum_adaptation_document: (
          values.result === 'Approved' &&
          application.process.kind !== consts.TransferProcessKind.CONVOCATION
        ) ? curriculumAdaptationDocument.content : undefined,
      }
    )
      .then(() => fetchApplication())
      .catch(error => {
        const response = (error as AxiosError).response;

        if (response && response.status === 400) {
          const errors = response.data;

          setCurriculumAdaptationDocument((prev) => ({ ...prev, error: false }))
          Object.keys(previousErrors).forEach((fieldName) => {
            if (formApi.fieldExists(fieldName)) {
              formApi.setError(fieldName, null);
            }
          });

          previousErrors = { ...errors };

          Object.entries(errors).forEach((e) => {
            if (e[0] === 'curriculum_adaptation_document') {
              setCurriculumAdaptationDocument((prev) => ({ ...prev, error: true }))
            }

            if (formApi.fieldExists(e[0])) {
              formApi.setError(e[0], e[1] as string[]);
            }
          });

          if (errors.non_field_errors) {
            toast.error(errors.non_field_errors.join(','))

          } else {
            toast.error('Ocorreu um erro ao deferir candidatura de transferência.')

          }

        } else {
          toast.error('Ocorreu um erro desconhecido ao deferir candidatura de transferência.')

        }
      })
      .finally(() => setSubmitting(false))
  }, [application, curriculumAdaptationDocument])

  useEffect(() => { if (token) fetchApplication() }, [token])
  useEffect(() => { if (token) fetchCoordinationDocumentHistory() }, [token, fetchCoordinationDocumentHistory])

  return (
    <PermissionContext.Consumer>
      {
        ({ hasPermission }: { hasPermission: (permission: string) => boolean }) => (
          <Card title={`Inscrição #${application ? application.id : ''} - Análise Coordenação`}>
            <Spinner active={loading}>
              <FieldSet title="Dados Pessoais" />
              <div className="enrollment-flex">
                <KeyValue title={application ? application.real_name : ''} label="Nome" />
                <KeyValue title={application ? application.assumed_name : ''} label="Nome Social" />
                <KeyValue title={application ? formater.cpf(application.cpf) : ''} label="CPF" />
                <KeyValue title={application ? application.email : ''} label="Email" />
                <KeyValue title={application ? formater.phone(application.phone) : ''} label="Telefone" />
                <KeyValue title={application ? application.birth_date : ''} label="Data de nascimento" />
              </div>
              <FieldSet title="Processo" />
              <div className="enrollment-flex">
                <KeyValue title={application ? application.process.name : ''} label="Nome" />
                <KeyValue title={application ? application.process.course.name : ''} label="Curso" />
                <KeyValue title={`${application ? application.desired_onboarding.field_step : ''}ᵒ`} label="Período pleiteado" />
                <KeyValue title={application ? application.status : ''} label="Situação" />
                <KeyValue title={application ? application.documentation_step_status : ''} label="Aprovação Secretaria" />
                <KeyValue title={application ? application.academic_step_status : ''} label="Aprovação Coordenação" />
                {application && application.status === 'Aprovado' && application.deferred_onboarding && <KeyValue title={`${application.deferred_onboarding.field_step}ᵒ`} label="Período deferido" />}
                {application && application.status === 'Aprovado' && <KeyValue title={application.approved_by} label="Deferido por" />}
                {application && application.status === 'Aprovado' && <KeyValue title={application.enrollment ? application.enrollment.status : 'Não criada'} label="Situação matricula" />}
                {application && application.status === 'Reprovado' && <KeyValue title={application.disapproved_by} label="Indeferido por" />}
                {application && application.status === 'Reprovado' && <KeyValue col="12" title={application.disapproval_reason} label="Motivo da reprovação" />}
              </div>
              <FieldSet title="Documentos" />
              <div className="documents-base row">
                <DocumentsView
                  personalDocuments={application ? application.documents : []}
                  personalDocumentsTypes={application ? application.documents.map(d => d.type) : []}
                />
              </div>
              {application
                && application.process.kind !== consts.TransferProcessKind.CONVOCATION
                && application.curriculum_adaptation_document && (
                <>
                  <FieldSet title="Adaptação curricular" />
                  <div className="documents-base row">
                    <DocumentsView
                      personalDocuments={application && application.curriculum_adaptation_document ? [application.curriculum_adaptation_document] : []}
                      personalDocumentsTypes={application ? [application.curriculum_adaptation_document_type] : []}
                    />
                  </div>
                </>
              )}
              {application
                && application.process.kind === consts.TransferProcessKind.CONVOCATION
                && application.process.course.name === 'Medicina'
                && coordinationDocuments.length > 0 && (
                <>
                  <FieldSet title="Documentos da Coordenação" />
                  {application.coordination_documents_step_status === 'Em análise' && (
                    <small className="d-block mb-2"><FontAwesomeIcon icon="circle-info" /> Aguardando a análise final da Secretaria.</small>
                  )}
                  {application.coordination_documents_step_status === 'Aprovado' && (
                    <small className="d-block mb-2"><FontAwesomeIcon icon="check-circle" className="green" /> Aprovado pela Secretaria. Fluxo encerrado.</small>
                  )}
                  {(application.issues || [])
                    .filter(p => p.category === 'Documentos da Coordenação' && p.status !== 'Resolvida')
                    .map(p => (
                      <div className="pendencies" key={p.id}>
                        <div className="pendencies-header">
                          <div className="mb-2">
                            <label className="pendencies-header-title">Pendência da Secretaria</label>
                          </div>
                        </div>
                        {/* pre-line: o conteúdo traz uma seção por documento, em linhas separadas. */}
                        <div className="pendencies-body" style={{ whiteSpace: 'pre-line' }}>
                          <p>{p.content}</p>
                        </div>
                      </div>
                    ))}
                  <div className="row">
                    {coordinationDocuments.map(group => (
                      <CoordinationDocumentCard
                        key={group.kind}
                        token={application.token}
                        group={group}
                        canUpload={
                          hasPermission('transfers.defer_application')
                          && application.status === 'Aprovado'
                          && application.coordination_documents_step_status !== 'Em análise'
                          && application.coordination_documents_step_status !== 'Aprovado'
                        }
                        onUploaded={fetchCoordinationDocumentHistory}
                      />
                    ))}
                  </div>
                  {hasPermission('transfers.defer_application')
                    && application.status === 'Aprovado'
                    && application.coordination_documents_step_status !== 'Em análise'
                    && application.coordination_documents_step_status !== 'Aprovado' && (
                    <div className="text-right mt-2">
                      <div className="col-md-4 p-0 offset-md-8 col-sm-12">
                        <div className="btn-actions">
                          {/* O botão desabilitado não recebe o hover: o tooltip fica no invólucro. */}
                          <span
                            className="w-100"
                            {...(missingCoordinationDocuments.length > 0
                              ? { 'data-tip': true, 'data-for': 'btn-send-for-review' }
                              : {})}
                          >
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={handleSendCoordinationDocumentsForReview}
                              disabled={sendingForReview || missingCoordinationDocuments.length > 0}
                              style={missingCoordinationDocuments.length > 0 ? { pointerEvents: 'none' } : undefined}
                            >
                              {sendingForReview ? 'Enviando...' : 'Enviar para análise da Secretaria'}
                            </button>
                          </span>
                          {missingCoordinationDocuments.length > 0 && (
                            <ReactTooltip id="btn-send-for-review" className="no-arrow-tooltip">
                              Para habilitar, envie: {missingCoordinationDocuments.join(' e ')}.
                            </ReactTooltip>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              <FieldSet title="Pendências" />
              {(application ? application.issues.filter(i => i.category === 'Acadêmica') : []).length === 0 && <p>Nenhuma pendência criada.</p>}
              {(application ? application.issues.filter(i => i.category === 'Acadêmica') : []).map(p => (
                <div className="pendencies">
                  <div className="pendencies-header">
                    <div className="mb-2">
                      <label className="pendencies-header-title">{issueLabel(p.id, p.created_by)}</label>
                      {p.status === 'Criada' && <FontAwesomeIcon icon="spinner" className="green ml-2 mt-1" />}
                      {p.status === 'Enviada' && <FontAwesomeIcon icon="envelope" className="green mt-1 ml-2" />}
                      {p.status === 'Resolvida' && <FontAwesomeIcon icon="check-circle" className="green mt-1 ml-2" />}
                      {p.suggested_onboarding && <small className="d-block"><em>Período sugerido:</em> {p.suggested_onboarding.field_step}ᵒ</small>}
                    </div>
                  </div>
                  <div className="pendencies-body">
                    <p>{p.content}</p>
                    {p.response && <p><b className="d-block">Resposta: </b>{p.response}</p>}
                  </div>
                  <div className="pendencies-date m-1">Resolvida em: {p.resolved_at || '-'}</div>
                  <div className="pendencies-date m-1">Enviada em: {p.sent_at || '-'}</div>
                  <div className="pendencies-date m-1">Criada em: {p.created_at}</div>
                </div>
              ))}
              {application && hasPermission('transfers.defer_application') && application.status === 'Em análise' && application.documentation_step_status === 'Aprovado' && (
                <Form key={application && application.id} initialValues={{ curriculum_adaptation_document: null }}>
                  {({ formState, formApi }: { formState: { values: { result?: 'Approved' | 'Reproved' | 'Suggested' } }, formApi: IFormApi }) => (
                    <>
                      <FieldSet title="Deferimento" />
                      <RadioGroup style={{ display: 'flex' }} field="result">
                        <div id="defer-options" className="radio-group">
                          <label className="input-radio-type" htmlFor="Approved">Aprovado
                            <Radio value="Approved" id="Approved" />
                            <span className="radiomark" />
                          </label>
                          <label className="input-radio-type" htmlFor="Reproved">Reprovado
                            <Radio value="Reproved" id="Reproved" />
                            <span className="radiomark" />
                          </label>
                          <label className="input-radio-type" htmlFor="Suggested">Sugerir alteração de período
                            <Radio value="Suggested" id="Suggested" />
                            <span className="radiomark" />
                          </label>
                        </div>
                      </RadioGroup>
                      {formState.values.result === 'Approved' && <small className="d-block"><FontAwesomeIcon icon="circle-info" /> Candidato será notificado e vinculado ao processo de matricula.</small>}
                      {formState.values.result === 'Reproved' && <small className="d-block"><FontAwesomeIcon icon="circle-info" /> Candidato será notificado e seu processo será encerrado.</small>}
                      {formState.values.result === 'Suggested' && <small className="d-block"><FontAwesomeIcon icon="circle-info" /> Uma pendência será criada e o candidato será notificado.</small>}
                      {formState.values.result === 'Suggested' && (
                        <TransferApplicationGradeInput
                          className="mt-1"
                          field="suggested_onboarding"
                          label="Período sugerido"
                          exclude={application && application.desired_onboarding.id}
                          processId={application && application.process.id}
                        />
                      )}
                      {formState.values.result && ['Reproved', 'Suggested'].includes(formState.values.result) && (
                        <InputTypeArea required className="mt-1" row="5" col="12" label="Motivo" field="reason" />
                      )}
                      {formState.values.result === 'Approved' && application.process.kind !== consts.TransferProcessKind.CONVOCATION && (
                        <div className="documents-base row">
                          <Documents
                            label={application.curriculum_adaptation_document_type.name}
                            validations={application.curriculum_adaptation_document_type.validations}
                            error={curriculumAdaptationDocument.error}
                            is_required
                            onUpload={(event: InputEvent) => handleUploadCurriculumAdaptationDocument(event)}
                            onRemove={(key: string) => console.log(key)}
                            documents={curriculumAdaptationDocument.content ? [curriculumAdaptationDocument.content] : []}
                          />
                        </div>
                      )}
                      {formState.values.result === 'Approved' && application.process.kind === consts.TransferProcessKind.CONVOCATION && (
                        <small className="d-block mt-1">
                          <FontAwesomeIcon icon="circle-info" /> Adaptação curricular: realizada pela Coordenação após a matrícula.
                        </small>
                      )}
                      <div className="text-right mt-2">
                        <div className="col-md-4 p-0 offset-md-8 col-sm-12">
                          <div className="btn-actions">
                            <button
                              id='btn-submit'
                              className={`btn btn-primary`}
                              onClick={() => handleDeferApplication(formState.values, formApi)}
                              disabled={submiting || !formState.values.result}
                              type="submit"
                            >
                              Deferir
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </Form>
              )}
            </Spinner>
          </Card>
        )}
    </PermissionContext.Consumer>
  )
}

export default TransferApplicationAcademicReviewPage;
